import { useState, useEffect, useCallback } from 'react';
import { adminGetOrders, adminUpdateOrderStatus } from '../../utils/api';
import { formatDate, ORDER_STATUS_COLORS } from '../../utils/helpers';
import toast from 'react-hot-toast';
import { FiEye, FiX, FiSearch, FiCopy, FiCheck } from 'react-icons/fi';
import styles from './AdminTable.module.css';

const STATUS_OPTIONS = [
  'pending', 'confirmed', 'processing', 'shipped',
  'delivered', 'cancelled', 'returned',
];

const MANUAL_METHODS = ['bkash', 'nagad', 'rocket'];

export default function AdminOrders() {
  const [orders, setOrders]             = useState([]);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(1);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newStatus, setNewStatus]       = useState('');
  const [statusNote, setStatusNote]     = useState('');
  const [updating, setUpdating]         = useState(false);
  const [copied, setCopied]             = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search)       params.search = search;
      if (statusFilter) params.status = statusFilter;
      const { data } = await adminGetOrders(params);
      setOrders(data.orders);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleStatusUpdate = async () => {
    if (!newStatus) { toast.error('Select a status'); return; }
    setUpdating(true);
    try {
      await adminUpdateOrderStatus(selectedOrder._id, { status: newStatus, note: statusNote });
      toast.success('Order status updated!');
      setSelectedOrder(null);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setUpdating(false);
    }
  };

  const openOrderModal = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setStatusNote('');
    setCopied(false);
  };

  const handleCopyTxnId = (txnId) => {
    navigator.clipboard.writeText(txnId);
    setCopied(true);
    toast.success('Transaction ID copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const pages = Math.ceil(total / 15);

  return (
    <div className={styles.page}>

      {/* Header */}
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Orders</h2>
          <p className={styles.pageSubtitle}>{total} total orders</p>
        </div>
      </div>

      {/* Search + filter */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <FiSearch size={15} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search by order # or customer..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className={styles.clearSearch} onClick={() => setSearch('')}>
              <FiX size={13} />
            </button>
          )}
        </div>
        <select
          className="form-input form-select"
          style={{ width: 'auto', fontSize: '.875rem' }}
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Status chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button
          className={`btn btn-sm ${!statusFilter ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => { setStatusFilter(''); setPage(1); }}
        >
          All
        </button>
        {STATUS_OPTIONS.map(s => {
          const c = ORDER_STATUS_COLORS[s] || {};
          return (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              style={{
                background:   statusFilter === s ? (c.bg || 'var(--bg)') : '#fff',
                color:        c.color || 'var(--text)',
                border:       `1.5px solid ${c.bg || 'var(--border)'}`,
                padding:      '4px 12px',
                borderRadius: 999,
                fontSize:     '.76rem',
                fontWeight:   600,
                cursor:       'pointer',
              }}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className={styles.tableCard}>
        {loading ? (
          <div className="spinner-center"><div className="spinner" /></div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan={8} className={styles.emptyRow}>No orders found</td>
                  </tr>
                ) : orders.map(o => {
                  const sc = ORDER_STATUS_COLORS[o.status] || {};
                  const isManual = MANUAL_METHODS.includes(o.paymentMethod);
                  return (
                    <tr key={o._id}>
                      <td>
                        <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '.875rem' }}>
                          #{o.orderNumber}
                        </span>
                      </td>
                      <td>
                        <p style={{ fontWeight: 600, fontSize: '.875rem' }}>
                          {o.user?.name || o.shippingAddress?.name || 'Guest'}
                        </p>
                        <p style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>
                          {o.shippingAddress?.phone}
                        </p>
                      </td>
                      <td style={{ fontSize: '.85rem' }}>{o.items?.length} item(s)</td>
                      <td>
                        <strong style={{ fontSize: '.875rem' }}>৳{o.totalPrice?.toLocaleString()}</strong>
                      </td>
                      <td>
                        {/* Payment method */}
                        <span style={{
                          fontSize: '.73rem', textTransform: 'uppercase',
                          fontWeight: 700, color: 'var(--text)',
                        }}>
                          {o.paymentMethod}
                        </span>
                        <br />
                        <span style={{
                          fontSize: '.7rem',
                          color: o.isPaid ? 'var(--success)' : 'var(--warning)',
                          fontWeight: 600,
                        }}>
                          {o.isPaid ? '✓ Paid' : '⏳ Unpaid'}
                        </span>

                        {/* Transaction ID pill in table */}
                        {isManual && o.paymentTransactionId && (
                          <span style={{
                            display:      'block',
                            marginTop:    3,
                            fontSize:     '.65rem',
                            fontFamily:   'monospace',
                            fontWeight:   700,
                            color:        '#166534',
                            background:   '#dcfce7',
                            border:       '1px solid #bbf7d0',
                            padding:      '1px 6px',
                            borderRadius: 4,
                            letterSpacing: '.3px',
                          }}>
                            {o.paymentTransactionId}
                          </span>
                        )}

                        {/* Warning if manual and no txnId */}
                        {isManual && !o.paymentTransactionId && !o.isPaid && (
                          <span style={{
                            display: 'block', marginTop: 3,
                            fontSize: '.65rem', color: 'var(--warning)', fontWeight: 600,
                          }}>
                            ⚠️ No TxnID
                          </span>
                        )}
                      </td>
                      <td>
                        <span
                          className="badge"
                          style={{ background: sc.bg, color: sc.color }}
                        >
                          {sc.label || o.status}
                        </span>
                      </td>
                      <td style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
                        {formatDate(o.createdAt)}
                      </td>
                      <td>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => openOrderModal(o)}
                        >
                          <FiEye size={14} /> Manage
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="pagination">
            {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                className={`page-btn ${p === page ? 'active' : ''}`}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Order Detail Modal ── */}
      {selectedOrder && (
        <div className={styles.modalOverlay} onClick={() => setSelectedOrder(null)}>
          <div
            className={styles.modal}
            style={{ maxWidth: 580 }}
            onClick={e => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div>
                <h3>Order #{selectedOrder.orderNumber}</h3>
                <span
                  className="badge"
                  style={{
                    background: ORDER_STATUS_COLORS[selectedOrder.status]?.bg,
                    color:      ORDER_STATUS_COLORS[selectedOrder.status]?.color,
                    marginTop:  4, display: 'inline-flex',
                  }}
                >
                  {ORDER_STATUS_COLORS[selectedOrder.status]?.label || selectedOrder.status}
                </span>
              </div>
              <button className={styles.modalClose} onClick={() => setSelectedOrder(null)}>
                <FiX size={20} />
              </button>
            </div>

            <div className={styles.modalForm}>

              {/* Order items */}
              <div style={{ marginBottom: '1.25rem' }}>
                <p style={{
                  fontSize: '.73rem', fontWeight: 700,
                  color: 'var(--text-secondary)', textTransform: 'uppercase',
                  letterSpacing: '.5px', marginBottom: '.625rem',
                }}>
                  Order Items
                </p>
                {selectedOrder.items?.map((item, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: '.625rem', alignItems: 'center',
                    padding: '.375rem 0', borderBottom: '1px solid var(--border)',
                  }}>
                    <img
                      src={item.image}
                      alt={item.name}
                      style={{ width: 38, height: 38, borderRadius: 7, objectFit: 'cover', flexShrink: 0 }}
                    />
                    <span style={{ flex: 1, fontSize: '.84rem', fontWeight: 500 }}>{item.name}</span>
                    <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>×{item.quantity}</span>
                    <strong style={{ fontSize: '.875rem' }}>৳{(item.price * item.quantity).toLocaleString()}</strong>
                  </div>
                ))}
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontWeight: 800, marginTop: '.625rem', fontSize: '.95rem',
                }}>
                  <span>Total</span>
                  <span style={{ color: 'var(--accent)' }}>৳{selectedOrder.totalPrice?.toLocaleString()}</span>
                </div>
              </div>

              {/* Shipping address */}
              <div style={{
                background: 'var(--bg)', padding: '.875rem',
                borderRadius: 8, marginBottom: '1rem',
              }}>
                <p style={{ fontWeight: 700, fontSize: '.875rem', marginBottom: '.375rem' }}>
                  📍 Delivery Address
                </p>
                <p style={{ fontSize: '.84rem', fontWeight: 600 }}>
                  {selectedOrder.shippingAddress?.name} — {selectedOrder.shippingAddress?.phone}
                </p>
                <p style={{ fontSize: '.82rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                  {selectedOrder.shippingAddress?.address}
                </p>
                <p style={{ fontSize: '.82rem', color: 'var(--text-secondary)' }}>
                  {selectedOrder.shippingAddress?.district}, {selectedOrder.shippingAddress?.division}
                  {selectedOrder.shippingAddress?.postalCode && ` — ${selectedOrder.shippingAddress.postalCode}`}
                </p>
              </div>

              {/* ── Transaction ID Block ── */}
              {(selectedOrder.paymentTransactionId || selectedOrder.note) && (
                <div style={{
                  background:    '#f0fdf4',
                  border:        '1.5px solid #86efac',
                  borderRadius:  10,
                  padding:       '1rem',
                  marginBottom:  '1rem',
                }}>
                  <p style={{
                    fontWeight: 700, fontSize: '.82rem',
                    color: '#166534', marginBottom: '.625rem',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    💳 Payment Transaction Details
                  </p>

                  {/* Transaction ID row */}
                  {selectedOrder.paymentTransactionId && (
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      gap: 8, flexWrap: 'wrap', marginBottom: 6,
                    }}>
                      <span style={{ fontSize: '.78rem', color: '#4b5563', fontWeight: 600 }}>
                        Transaction ID:
                      </span>
                      <span style={{
                        fontFamily:    'monospace',
                        fontWeight:    800,
                        fontSize:      '.95rem',
                        color:         '#166534',
                        background:    '#dcfce7',
                        border:        '1px solid #86efac',
                        padding:       '3px 12px',
                        borderRadius:  7,
                        letterSpacing: '.5px',
                      }}>
                        {selectedOrder.paymentTransactionId}
                      </span>
                      <button
                        onClick={() => handleCopyTxnId(selectedOrder.paymentTransactionId)}
                        style={{
                          display:       'flex',
                          alignItems:    'center',
                          gap:           4,
                          background:    copied ? '#16a34a' : '#fff',
                          border:        '1px solid #86efac',
                          borderRadius:  7,
                          padding:       '3px 10px',
                          fontSize:      '.72rem',
                          cursor:        'pointer',
                          color:         copied ? '#fff' : '#166534',
                          fontWeight:    700,
                          transition:    'all .2s',
                        }}
                      >
                        {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  )}

                  {/* Note */}
                  {selectedOrder.note && (
                    <p style={{
                      fontSize: '.78rem', color: '#4b5563',
                      background: '#fff', padding: '6px 10px',
                      borderRadius: 6, border: '1px solid #d1fae5',
                      marginBottom: 6,
                    }}>
                      📝 {selectedOrder.note}
                    </p>
                  )}

                  {/* Verify reminder */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    marginTop: 4,
                  }}>
                    <span style={{
                      background: '#fef3c7', color: '#92400e',
                      fontSize: '.7rem', fontWeight: 700,
                      padding: '3px 8px', borderRadius: 6,
                    }}>
                      ⚠️ Verify Required
                    </span>
                    <span style={{ fontSize: '.72rem', color: '#6b7280' }}>
                      Check your {selectedOrder.paymentMethod?.toUpperCase()} app before confirming this order.
                    </span>
                  </div>
                </div>
              )}

              {/* No transaction ID warning for manual methods */}
              {MANUAL_METHODS.includes(selectedOrder.paymentMethod) &&
               !selectedOrder.paymentTransactionId &&
               !selectedOrder.isPaid && (
                <div style={{
                  background:   '#fef3c7', border: '1.5px solid #fcd34d',
                  borderRadius: 8, padding: '.75rem', marginBottom: '1rem',
                  fontSize:     '.8rem', color: '#92400e', fontWeight: 600,
                }}>
                  ⚠️ No transaction ID provided. Contact the customer to verify payment before confirming.
                </div>
              )}

              {/* Payment info row */}
              <div style={{
                display: 'flex', gap: '1rem', flexWrap: 'wrap',
                background: 'var(--bg)', padding: '.75rem',
                borderRadius: 8, marginBottom: '1rem', fontSize: '.8rem',
              }}>
                <span><strong>Method:</strong> {selectedOrder.paymentMethod?.toUpperCase()}</span>
                <span>
                  <strong>Status:</strong>{' '}
                  <span style={{ color: selectedOrder.isPaid ? 'var(--success)' : 'var(--warning)', fontWeight: 700 }}>
                    {selectedOrder.isPaid ? '✓ Paid' : '⏳ Pending'}
                  </span>
                </span>
                <span><strong>Subtotal:</strong> ৳{selectedOrder.itemsPrice?.toLocaleString()}</span>
                <span><strong>Shipping:</strong> ৳{selectedOrder.shippingPrice}</span>
              </div>

              {/* Update status */}
              <div className="form-group" style={{ marginBottom: '.875rem' }}>
                <label className="form-label">Update Order Status</label>
                <select
                  className="form-input form-select"
                  value={newStatus}
                  onChange={e => setNewStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map(s => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">Internal Note (optional)</label>
                <input
                  className="form-input"
                  value={statusNote}
                  onChange={e => setStatusNote(e.target.value)}
                  placeholder="e.g. Verified bKash payment, dispatched via SA Paribahan"
                />
              </div>

            </div>

            <div className={styles.modalFooter}>
              <button className="btn btn-ghost" onClick={() => setSelectedOrder(null)}>
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={handleStatusUpdate}
                disabled={updating}
              >
                {updating ? (
                  <><div className="spinner spinner-sm" /> Updating…</>
                ) : (
                  'Update Status'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
